/**
 * Captures the first usable frame from a video as a data URL
 * 
 * @param src - Video source URL
 * @param seekTime - Time in seconds to seek to (default: 0.5s to avoid black frames)
 * @returns Promise that resolves to data URL or undefined if capture fails
 */
export async function captureFirstFrame(src: string, seekTime = 0.5): Promise<string | undefined> {
  return new Promise<string | undefined>((resolve) => {
    if (!src || typeof document === 'undefined') {
      return resolve(undefined);
    }

    const video = document.createElement('video');
    video.src = src;
    video.crossOrigin = 'anonymous';
    video.muted = true;

    const onError = () => resolve(undefined);
    
    const onSeeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 16;
        canvas.height = video.videoHeight || 9;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          resolve(dataUrl);
        } else {
          resolve(undefined);
        }
      } catch (e) {
        resolve(undefined);
      }
      cleanup();
    };

    const onLoaded = () => {
      try {
        video.currentTime = Math.min(seekTime, video.duration || seekTime);
      } catch {
        setTimeout(() => {
          try {
            video.currentTime = seekTime;
          } catch {
            // Ignore
          }
        }, 250);
      }
    };

    const cleanup = () => {
      video.removeEventListener('error', onError);
      video.removeEventListener('loadeddata', onLoaded);
      video.removeEventListener('seeked', onSeeked);
    };

    video.addEventListener('error', onError);
    video.addEventListener('loadeddata', onLoaded);
    video.addEventListener('seeked', onSeeked);

    // Fallback timeout
    setTimeout(() => resolve(undefined), 3000);
  });
}
